export const EUL_TYPE_OPTIONS = [
  { value: 'B10', label: 'B10 (90%信頼性)' },
  { value: 'B20', label: 'B20 (80%信頼性)' },
  { value: 'B30', label: 'B30 (70%信頼性) - デフォルト' },
  { value: 'B50', label: 'B50 (50%信頼性)' },
  { value: 'UNKNOWN', label: '不明' },
] as const;

export type EulType = typeof EUL_TYPE_OPTIONS[number]['value'];

export const DEFAULT_EUL_TYPE: EulType = 'B30';

export const EUL_TYPE_HELP_TEXT: Record<EulType, string> = {
  B10: 'ベアリングなど高信頼性が求められる部品・消耗品の指標値です',
  B20: '産業機械の主要コンポーネントで用いられる指標です',
  B30: '一般的な機械・設備に用いられる指標です（デフォルト）',
  B50: '電灯など消耗品に用いられる指標です',
  UNKNOWN: '指標が不明な場合に選択してください（入力値をそのまま使用します）',
};

type ConversionFactor = {
  lnFactor: number | null;
};

const EUL_CONVERSION_FACTORS: Record<EulType, ConversionFactor> = {
  B10: { lnFactor: 0.10536 },
  B20: { lnFactor: 0.22314 },
  B30: { lnFactor: 0.35667 },
  B50: { lnFactor: 0.69315 },
  UNKNOWN: { lnFactor: null },
};

const roundToTwoDecimals = (value: number) => Math.round(value * 100) / 100;

export const isEulType = (value: unknown): value is EulType => {
  return typeof value === 'string' && (EUL_TYPE_OPTIONS as ReadonlyArray<{ value: string }>).some((option) => option.value === value);
};

export function calculateEulB632(eulInput: number | null | undefined, eulType: EulType, beta: number = 2): number | null {
  if (eulInput == null || Number.isNaN(eulInput) || eulInput <= 0) {
    return null;
  }
  const normalizedType: EulType = isEulType(eulType) ? eulType : DEFAULT_EUL_TYPE;
  if (normalizedType === 'UNKNOWN') {
    return roundToTwoDecimals(eulInput);
  }

  const factor = EUL_CONVERSION_FACTORS[normalizedType];
  if (!factor?.lnFactor) {
    return roundToTwoDecimals(eulInput);
  }

  const normalizedBeta = beta > 0 && Number.isFinite(beta) ? beta : 2;
  const conversionCoefficient = Math.pow(factor.lnFactor, 1 / normalizedBeta);
  return roundToTwoDecimals(eulInput / conversionCoefficient);
}

export function getEulTypeLabel(value: string | null | undefined): string {
  const match = EUL_TYPE_OPTIONS.find((option) => option.value === value);
  if (match) {
    return match.label;
  }
  const fallback = EUL_TYPE_OPTIONS.find((option) => option.value === DEFAULT_EUL_TYPE);
  return fallback ? fallback.label : DEFAULT_EUL_TYPE;
}
